import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ArrowUp, ArrowDown, RotateCcw, Clock, Sliders,
  ChevronLeft, ChevronRight, AlertCircle, Inbox,
} from 'lucide-react';
import { getLoyaltyHistory } from '../services/loyaltyService';
import { TX_TYPES } from '../../../domain/loyaltyCalculations';
import { useAuth } from '../../../context/AuthContext';

const TX_TYPE_META = {
  [TX_TYPES.EARN]:    { icon: ArrowUp,    color: 'text-green-700',  bg: 'bg-green-50',  border: 'border-green-200',  labelKey: 'loyalty.txEarn' },
  [TX_TYPES.REDEEM]:  { icon: ArrowDown,  color: 'text-blue-700',   bg: 'bg-blue-50',   border: 'border-blue-200',   labelKey: 'loyalty.txRedeem' },
  [TX_TYPES.REVERSE]: { icon: RotateCcw,  color: 'text-orange-700', bg: 'bg-orange-50', border: 'border-orange-200', labelKey: 'loyalty.txReverse' },
  [TX_TYPES.EXPIRE]:  { icon: Clock,      color: 'text-red-700',    bg: 'bg-red-50',    border: 'border-red-200',    labelKey: 'loyalty.txExpire' },
  [TX_TYPES.ADJUST]:  { icon: Sliders,    color: 'text-purple-700', bg: 'bg-purple-50', border: 'border-purple-200', labelKey: 'loyalty.txAdjust' },
};

const PAGE_SIZE = 10;

const formatDate = (value, locale = 'tr-TR') => {
  if (!value) return '—';
  try {
    const date = value.toDate
      ? value.toDate()
      : value.seconds
      ? new Date(value.seconds * 1000)
      : new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return '—';
  }
};

const TransactionRow = ({ tx }) => {
  const { t, i18n } = useTranslation('customers');
  const numLocale = i18n.language === 'tr' ? 'tr-TR' : 'en-US';
  const dateLocale = i18n.language === 'tr' ? 'tr-TR' : 'en-GB';
  const meta = TX_TYPE_META[tx.type] || TX_TYPE_META[TX_TYPES.ADJUST];
  const Icon = meta.icon;
  const isNegative = (Number(tx.points) || 0) < 0;
  const absPoints = Math.abs(Number(tx.points) || 0);

  return (
    <div className={`flex items-start gap-3 p-3 rounded-lg border ${meta.border} ${meta.bg} transition-colors`}>
      <div className={`flex-shrink-0 p-2 rounded-full bg-white ${meta.color}`}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <span className={`text-sm font-semibold ${meta.color}`}>{t(meta.labelKey)}</span>
          <span className={`text-sm font-bold ${isNegative ? 'text-red-600' : 'text-green-600'}`}>
            {isNegative ? '-' : '+'}{absPoints.toLocaleString(numLocale)} {t('loyalty.pointsSuffix')}
          </span>
        </div>
        <p className="text-xs text-gray-600 mt-0.5 truncate" title={tx.description}>{tx.description || '—'}</p>
        <div className="flex items-center justify-between gap-2 mt-1.5">
          <span className="text-[11px] text-gray-500">{formatDate(tx.createdAt, dateLocale)}</span>
          {typeof tx.balanceAfter === 'number' && (
            <span className="text-[11px] text-gray-500">
              {t('loyalty.balance')} <span className="font-medium text-gray-700">{tx.balanceAfter.toLocaleString(numLocale)}</span>
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

const LoyaltyHistoryList = ({ customerId }) => {
  const { t } = useTranslation('customers');
  const { user } = useAuth();
  const [pages, setPages] = useState([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadFirstPage = useCallback(async () => {
    if (!customerId) return;
    setLoading(true);
    setError(null);
    try {
      const result = await getLoyaltyHistory(customerId, PAGE_SIZE, null, user?.businessId);
      setPages([result]);
      setCurrentIdx(0);
    } catch (err) {
      console.error('Loyalty history load error:', err);
      setError(t('loyalty.loadError'));
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  useEffect(() => { loadFirstPage(); }, [loadFirstPage]);

  const current = pages[currentIdx];
  const canGoNext = current?.hasMore || currentIdx < pages.length - 1;
  const canGoPrev = currentIdx > 0;

  const handleNext = async () => {
    if (currentIdx < pages.length - 1) { setCurrentIdx(currentIdx + 1); return; }
    if (!current?.hasMore || loading) return;
    setLoading(true);
    try {
      const result = await getLoyaltyHistory(customerId, PAGE_SIZE, current.lastDoc, user?.businessId);
      setPages((prev) => [...prev, result]);
      setCurrentIdx((idx) => idx + 1);
    } catch (err) {
      console.error('Loyalty history next page error:', err);
      setError(t('loyalty.nextPageError'));
    } finally {
      setLoading(false);
    }
  };

  const handlePrev = () => { if (canGoPrev) setCurrentIdx(currentIdx - 1); };

  if (loading && pages.length === 0) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500" />
        <span className="ml-3 text-sm text-gray-600">{t('loyalty.loading')}</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center py-8 text-center">
        <AlertCircle className="h-8 w-8 text-red-400 mb-2" />
        <p className="text-sm text-gray-600">{error}</p>
        <button onClick={loadFirstPage} className="mt-3 text-sm text-blue-600 hover:text-blue-700 underline">
          {t('loyalty.retry')}
        </button>
      </div>
    );
  }

  if (!current || current.transactions.length === 0) {
    return (
      <div className="flex flex-col items-center py-12 text-center">
        <Inbox className="h-10 w-10 text-gray-300 mb-3" />
        <p className="text-sm font-medium text-gray-600">{t('loyalty.emptyTitle')}</p>
        <p className="text-xs text-gray-500 mt-1">{t('loyalty.emptyDesc')}</p>
      </div>
    );
  }

  const totalShown = currentIdx * PAGE_SIZE + current.transactions.length;

  return (
    <div className="space-y-3">
      <div className="space-y-2">
        {current.transactions.map((tx) => <TransactionRow key={tx.id} tx={tx} />)}
      </div>
      <div className="flex items-center justify-between pt-2 border-t border-gray-200">
        <span className="text-xs text-gray-500">
          {t('loyalty.pageInfo', { page: currentIdx + 1, count: totalShown })}
        </span>
        <div className="flex items-center gap-1">
          <button onClick={handlePrev} disabled={!canGoPrev || loading}
            className="p-1.5 text-gray-600 hover:bg-gray-100 rounded-md disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            aria-label={t('loyalty.prevPage')}>
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button onClick={handleNext} disabled={!canGoNext || loading}
            className="p-1.5 text-gray-600 hover:bg-gray-100 rounded-md disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            aria-label={t('loyalty.nextPage')}>
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
      {loading && pages.length > 0 && (
        <div className="flex justify-center pt-1">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-400" />
        </div>
      )}
    </div>
  );
};

export default LoyaltyHistoryList;
